import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useEffect, useRef, useState } from "react";
import { Fetch } from "../../../shared/Fetch";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import MonsterCandidateProfile from "./Monstercandidateprofile";
import { Dialog } from "primereact/dialog";
import MonsterBooleanSearchFilter from "./MonsterBooleanSearchFilter";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Root {
  "?xml"?: XmlInfo;
  monster?: Monster;
}
export interface XmlInfo {
  version?: string;
  encoding?: string;
}
export interface Monster {
  resumes?: Resumes;
}
export interface Resumes {
  found?: string;
  returned?: string;
  status?: string;
  resume?: Resume[];
}
export interface Resume {
  sid?: string;
  relevance?: string;
  resumeTitle?: string;
  dateModified?: DateInfo;
  dateCreated?: DateInfo;
  personalData?: PersonalData;
  target?: Target;
  educations?: Education[];
  experiences?: Experience[];
  workAuths?: WorkAuth[];
  boards?: Board[];
}
export interface Board {
  id?: string;
  text?: string;
}
export interface Education {
  levelId?: string;
  level?: string;
}
export interface DateInfo {
  date?: string;
  text?: string;
}
export interface PersonalData {
  confidential?: string;
  emailHash?: string;
  name?: Name;
  address?: Address;
  militaryExperience?: MilitaryExperience;
}
export interface Name {
  first?: string;
  middle?: string;
  last?: string;
}
export interface Address {
  country?: IdWrapper;
  state?: IdWrapper | null;
  city?: string;
  postalCode?: string;
  location?: string;
}
export interface MilitaryExperience {
  serviceFlag?: string;
  militaryInvolvement?: string;
}
export interface Target {
  jobTitle?: string;
  relocation?: IdText;
  salary?: Salary;
  jobTypes?: any;
}
export interface Salary {
  min?: string;
  max?: string;
  type?: IdWrapper | string;
  currency?: IdWrapper;
}
export interface Experience {
  companyName?: string;
  jobTitle?: string;
  description?: string;
  dateFrom?: DatePart;
  dateTo?: DatePart | null;
}
export interface DatePart {
  "@Year"?: string;
  "@Month"?: string;
}
export interface WorkAuth {
  countryId?: string;
  countryText?: string;
  authTypeId?: string;
  authTypeText?: string;
}
export interface IdWrapper {
  id?: string;
  lid?: string;
}
export interface IdText {
  id?: string;
  text?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ProfileSkeleton = () => (
  <div style={{ padding: "16px" }}>
    <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
      <Skeleton shape="circle" size="64px" />
      <div style={{ flex: 1 }}>
        <Skeleton width="200px" height="20px" style={{ marginBottom: "8px" }} />
        <Skeleton width="140px" height="16px" style={{ marginBottom: "8px" }} />
        <Skeleton width="180px" height="16px" />
      </div>
    </div>
    <Skeleton width="100%" height="120px" style={{ marginBottom: "16px" }} />
    <Skeleton width="100%" height="80px" style={{ marginBottom: "16px" }} />
    <Skeleton width="100%" height="80px" />
  </div>
);

const BOOLEAN_OPERATORS = new Set(["and", "or", "not"]);
const parseKeywords = (keywords: string): string[] => {
  const terms: string[] = [];

  const quotedRegex = /"([^"]+)"/g;
  let match;
  while ((match = quotedRegex.exec(keywords)) !== null) {
    const phrase = match[1].trim();
    if (phrase.length > 0) terms.push(phrase);
  }
  const withoutQuotes = keywords.replace(/"[^"]*"/g, "");
  const singleWords = withoutQuotes
    .split(/[\s,]+/)
    .map((k) => k.replace(/^[(]+|[')]+$/g, "").trim())
    .filter((k) => k.length > 0 && !BOOLEAN_OPERATORS.has(k.toLowerCase()));

  terms.push(...singleWords);
  return terms;
};

const termToPattern = (t: string): string => {
  const esc = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return `(?<!\\w)${esc}(?!\\w)`;
};

const highlightText = (text: string, keywords: string): React.ReactNode => {
  if (!keywords?.trim()) return text;
  const terms = parseKeywords(keywords);
  if (!terms.length) return text;

  const regex = new RegExp(`(${terms.map(termToPattern).join("|")})`, "gi");
  const parts = text.split(regex);
  const testRegex = new RegExp(`^(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})$`, "i");

  return parts.map((part, i) =>
    testRegex.test(part) ? (
      <mark key={i} style={{ background: "#fff176", color: "#1a1d23", borderRadius: "3px", padding: "0 2px", fontWeight: 700 }}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

// ─── Build RSX query string from filters ─────────────────────────────────────

const buildFilterParams = (filters: any, booleanQuery: string): Record<string, any> => {
  console.log("buildFilterParams called with filters:", filters);
  const params: Record<string, any> = {};

  // Build q from boolean query + job title + skills
  let q = booleanQuery?.trim() || "";

  if (filters.jobTitle?.trim() && !q.toLowerCase().includes(filters.jobTitle.trim().toLowerCase())) {
    q = q ? `(${q}) AND "${filters.jobTitle.trim()}"` : `"${filters.jobTitle.trim()}"`;
  }

  if (filters.skillKeywords?.trim()) {
    q = q ? `(${q}) AND (${filters.skillKeywords.trim()})` : filters.skillKeywords.trim();
  }

  if (q) params.query = q;

  // Location
  const stateZipMap: any = {
    Texas: "75001",
    California: "90001",
    Florida: "32004",
    NewYork: "10001",
    Alabama: "36101",
    Alaska: "99501",
    Arizona: "85001",
    Arkansas: "72201",
    Colorado: "80001",
    Connecticut: "06101",
    Delaware: "19901",
    Georgia: "30301",
    Hawaii: "96701",
    Idaho: "83201",
    Illinois: "60007",
    Indiana: "46201",
    Iowa: "50001",
    Kansas: "66002",
    Kentucky: "40003",
    Louisiana: "70001",
    Maine: "03901",
    Maryland: "20601",
    Massachusetts: "01001",
    Michigan: "48001",
    Minnesota: "55001",
    Mississippi: "38601",
    Missouri: "63001",
    Montana: "59001",
    Nebraska: "68001",
    Nevada: "88901",
    NewHampshire: "03031",
    NewJersey: "07001",
    NewMexico: "87001",
    NorthCarolina: "27006",
    NorthDakota: "58001",
    Ohio: "43001",
    Oklahoma: "73001",
    Oregon: "97001",
    Pennsylvania: "15001",
    RhodeIsland: "02801",
    SouthCarolina: "29001",
    SouthDakota: "57001",
    Tennessee: "37010",
    Utah: "84001",
    Vermont: "05001",
    Virginia: "20101",
    Washington: "98001",
    WestVirginia: "24701",
    Wisconsin: "53001",
    Wyoming: "82001",
  };

  if (Array.isArray(filters.location) && filters.location.length > 0) {
    params.where = filters.location
      .map((loc: any) => loc.zipCode)
      .filter(Boolean)
      .join("; ");
  } else if (typeof filters.location === "string" && filters.location.trim()) {
    params.where = filters.location.trim();
  }
  if (filters.radius && filters.radius !== "0") params.radius = filters.radius;

  // Resume updated
  if (filters.resumeUpdatedRange && filters.resumeUpdatedRange !== "custom") {
    params.resumeUpdatedWithin = filters.resumeUpdatedRange;
  }
  if (filters.resumeUpdatedRange === "custom" && filters.resumeUpdatedFrom) {
    const fromDate = filters.resumeUpdatedFrom instanceof Date ? filters.resumeUpdatedFrom : new Date(filters.resumeUpdatedFrom);
    const diffDays = Math.ceil((new Date().getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
    params.resumeUpdatedWithin = String(Math.max(diffDays, 1));
  }

  // Candidate preferences
  if (filters.relocation != null && filters.relocation !== "") params.reloc = filters.relocation;
  if (filters.workAuth != null && filters.workAuth !== "") params.wauth = filters.workAuth;
  if (filters.educationLevel != null && filters.educationLevel !== "") params.edulv = filters.educationLevel;
  if (filters.militaryExperience) params.mil = filters.militaryExperience;

  // Most recent experience
  if (filters.mostRecentEmployer?.trim()) params.employer = filters.mostRecentEmployer.trim();
  if (filters.mostRecentJobTitle?.trim()) params.rjt = filters.mostRecentJobTitle.trim();
  if (filters.mostRecentJobDescription?.trim()) params.rjd = filters.mostRecentJobDescription.trim();

  // Education
  if (filters.schoolAttended?.trim()) params.school = filters.schoolAttended.trim();

  // Salary (uncomment when ready)
  if (filters.salaryType != null && filters.salaryType !== "") params.styp = filters.salaryType;
  if (filters.salaryMin?.trim()) params.smin = filters.salaryMin.trim();
  if (filters.salaryMax?.trim()) params.smax = filters.salaryMax.trim();

  return params;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const MonsterBooleanSearch = () => {
  const [booleanSearchQuery, setBooleanSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState({} as Root);
  const [totalResults, setTotalResults] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [profileVisible, setProfileVisible] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [searchHistory, setSearchHistory] = useState<{ id: number; query: string; dateTime: string; resultCount: number | null }[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [useageData, setUseageData] = useState({
    customerId: 0,
    quota: 0,
    viewsConsumed: 0,
    viewsRemaining: 0,
  });
  const toast = useRef<any>(null);

  const rowsPerPage = 25;
  const totalPages = Math.ceil(totalResults / rowsPerPage);
  const groupSize = 5;
  const currentGroup = Math.floor((page - 1) / groupSize);
  const startPage = currentGroup * groupSize + 1;
  const endPage = Math.min(startPage + groupSize - 1, totalPages);

  const checkUsageBeforeSearch = async () => {
    try {
      const response = await Fetch("Monster/getViewAccess", "GET", null, `profile_ID=forViewCount`, toast);
      if (response) {
        const data = await response.json();
        if (data.isAccess === true) {
          setUseageData({
            customerId: 0,
            quota: data.maxViewsAllowed || 0,
            viewsConsumed: data.viewsConsumed || 0,
            viewsRemaining: (data.maxViewsAllowed || 0) - (data.viewsConsumed || 0),
          });
        } else if (data.isAccess === false) {
          toast?.current.show({
            severity: "error",
            detail: data.message || "Something Went Wrong or You Do Not Have View Limit",
            life: 3000,
          });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadTableData = async (pageNum?: number, filtersOverride?: any) => {
    if (!booleanSearchQuery?.trim() && !activeFilters?.jobTitle?.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Empty Search",
        detail: "Please enter a Boolean Search Query or at least a Job Title.",
        life: 2500,
      });
      return;
    }

    setIsLoading(true);
    if (pageNum == null) setPage(1);
    const pgNum = pageNum != null && pageNum > 0 ? pageNum : page;

    const filters = filtersOverride ?? activeFilters;
    const filterParams = buildFilterParams(filters, booleanSearchQuery);
    console.log("Sending to backend:", { ...filterParams, PerPageResp: rowsPerPage, Page: pgNum === 1 ? 0 : (pgNum - 1) * rowsPerPage });

    const response = await Fetch(
      "Monster/getBooleanSearchResponse",
      "POST",
      {
        ...filterParams,
        PerPageResp: rowsPerPage,
        // Page: pgNum === 0 ? 0 : (pgNum - 1) * rowsPerPage,
        Page: pgNum,
      },
      null,
      toast,
    );

    if (response) {
      const data: Root = await response.json();
      setResults(data);
      setShowResults(true);
      setTotalResults(Number(data?.monster?.resumes?.found || "0"));
      fetchSearchHistory();
    }
    setIsLoading(false);
  };

  const handleApplyFilters = async (filters: any) => {
    setActiveFilters(filters);
    setPage(1);
    await checkUsageBeforeSearch();
    await loadTableData(1, filters);
  };

  const handleNewSearch = () => {
    setBooleanSearchQuery("");
    setShowResults(false);
    setResults({});
    setTotalResults(0);
    setPage(1);
    setActiveFilters({});
    setFilterResetKey((k) => k + 1);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  function getPaginationRange(totalRecords: number, pageSize: number, currentPage: number) {
    const start = (currentPage - 1) * pageSize + 1;
    let end = Math.min(currentPage * pageSize, totalRecords);
    return `${start} - ${end}`;
  }

  const onPage = (pageNum: number) => {
    setPage(pageNum);
    loadTableData(pageNum);
  };

  const openResume = async (resume: Resume) => {
    const sid = resume?.sid;
    if (!sid) {
      toast.current?.show({ severity: "warn", summary: "Missing ID", detail: "No resume ID available.", life: 3000 });
      return;
    }
    try {
      const response = await Fetch("Monster/getViewAccess", "GET", null, `profile_ID=${sid}`, toast);
      if (!response) return;
      const data = await response.json();
      if (data.isAccess) {
        setSelectedResume(null);
        setProfileLoading(true);
        setProfileVisible(true);
        setTimeout(() => setSelectedResume(resume), 0);
        setUseageData({
          customerId: 0,
          quota: data.maxViewsAllowed || 0,
          viewsConsumed: data.viewsConsumed || 0,
          viewsRemaining: (data.maxViewsAllowed || 0) - (data.viewsConsumed || 0),
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Access Denied",
          detail: data.message || "You do not have access.",
          life: 4000,
        });
      }
    } catch (err) {
      console.error("getViewAccess error:", err);
    }
  };

  const checkUsageBeforeSearch2 = async () => {
    try {
      const response = await Fetch("Monster/getViewAccess", "GET", null, `profile_ID=forViewCount`, toast);
      if (response) {
        const data = await response.json();
        if (data.isAccess === true) {
          setUseageData({
            customerId: 0,
            quota: data.maxViewsAllowed || 0,
            viewsConsumed: data.viewsConsumed || 0,
            viewsRemaining: (data.maxViewsAllowed || 0) - (data.viewsConsumed || 0),
          });
        } else if (data.isAccess === false) {
          toast?.current.show({ severity: "error", detail: data.message || "Something Went Wrong or Your Do Not Have View Limit", life: 3000 });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSearchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await Fetch("Monster/getSearchHistory", "GET", null, null, toast);
      if (response) {
        const data = await response.json();
        setSearchHistory(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // useEffect(() => {
  //   checkUsageBeforeSearch2();
  // }, []);
  useEffect(() => {
    const init = async () => {
      await checkUsageBeforeSearch2();
      await fetchSearchHistory();
    };
    init();
  }, []);

  return (
    <>
      {/* ── Search Header Bar ─────────────────────────────────────────────── */}
      {!showResults && (
        <div
          style={{
            position: "relative",
            backgroundColor: "#1e0a3c",
            padding: "48px 32px 52px",
            overflow: "hidden",
          }}
        >
          {/* Background pattern */}
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.18, pointerEvents: "none" }} xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="geo" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                <polygon points="0,0 40,0 0,40" fill="none" stroke="#a78bfa" strokeWidth="1" />
                <polygon points="80,80 40,80 80,40" fill="none" stroke="#a78bfa" strokeWidth="1" />
                <circle cx="60" cy="20" r="14" fill="none" stroke="#a78bfa" strokeWidth="1" />
                <circle cx="20" cy="60" r="14" fill="none" stroke="#a78bfa" strokeWidth="1" />
                <line x1="0" y1="80" x2="80" y2="0" stroke="#a78bfa" strokeWidth="0.5" />
                <polygon points="40,0 80,0 80,40" fill="#6d28d9" opacity="0.4" />
                <polygon points="0,40 0,80 40,80" fill="#6d28d9" opacity="0.4" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#geo)" />
          </svg>

          <div
            style={{
              position: "relative",
              maxWidth: "1100px",
              margin: "0 auto",
              backgroundColor: "#fff",
              borderRadius: "6px",
              display: "flex",
              overflow: "hidden",
              boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
            }}
          >
            <div style={{ flex: "1 1 0", borderRight: "1px solid #e5e7eb", padding: "10px 14px" }}>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#6b21a8",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: "4px",
                }}
              >
                BOOLEAN SEARCH
              </div>
              <InputText
                value={booleanSearchQuery}
                onChange={(e) => setBooleanSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setShowResults(true);
                    // checkUsageBeforeSearch().then(() => loadTableData());
                    checkUsageBeforeSearch().then(() => loadTableData(1, activeFilters));
                  }
                }}
                placeholder="(e.g. Sales AND Marketing NOT Audit)"
                className="w-full border-none shadow-none p-0"
                style={{ fontSize: "16px", color: "black", fontWeight: "bolder" }}
              />
            </div>
            <Button
              label="SEARCH"
              onClick={async () => {
                setShowResults(true);
                await checkUsageBeforeSearch();
                // await loadTableData();
                await loadTableData(1, activeFilters);
              }}
              style={{
                backgroundColor: "#9333ea",
                border: "none",
                borderRadius: "0",
                fontSize: "15px",
                fontWeight: 700,
                padding: "0 28px",
                letterSpacing: "0.05em",
                flexShrink: 0,
              }}
            />
          </div>
        </div>
      )}

      {/* ── Search History ───────────────────────────────────────────────── */}
      {!showResults && (
        <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "20px 32px 8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "12px" }}>
            <i className="pi pi-clock" style={{ fontSize: 14, color: "#9333ea" }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#6b7280" }}>Recent Searches</span>
          </div>
          <DataTable
            value={searchHistory}
            size="small"
            rowHover
            scrollable
            scrollHeight="300px"
            loading={historyLoading}
            onRowClick={(e) => {
              setBooleanSearchQuery(e.data.search_Query);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            emptyMessage={
              <div style={{ textAlign: "center", padding: "28px 0", color: "#9ca3af", fontSize: "13px" }}>
                <i className="pi pi-search" style={{ fontSize: 22, display: "block", marginBottom: 8, color: "#d8b4fe" }} />
                No recent searches yet.
              </div>
            }
            style={{
              fontSize: "12px",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
              cursor: "pointer",
            }}
          >
            <Column
              header="Search Query"
              style={{ width: "75%" }}
              body={(row) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="pi pi-search" style={{ fontSize: 11, color: "#c4b5fd" }} />
                  <span
                    title={row.search_Query}
                    style={{
                      fontFamily: "monospace",
                      fontSize: "12px",
                      fontWeight: 600,
                      background: "#f5f0ff",
                      color: "#4c1d95",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      maxWidth: "500px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                    }}
                  >
                    {row.search_Query}
                  </span>
                </div>
              )}
            />

            <Column
              header="Searched At"
              style={{ width: "20%" }}
              body={(row) => (
                <span style={{ fontSize: "11px", color: "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
                  <i className="pi pi-calendar" style={{ fontSize: 11, color: "#c4b5fd" }} />
                  {row.created_At
                    ? new Date(row.created_At).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—"}
                </span>
              )}
            />
          </DataTable>
        </div>
      )}

      {/* ── Layout: Sidebar + Results ─────────────────────────────────────── */}
      <div style={{ display: "flex", backgroundColor: "#f3f4f6", minHeight: "calc(100vh - 160px)" }}>
        {/* ── LEFT FILTER SIDEBAR ─────────────────────────────────────────── */}
        {showResults && <MonsterBooleanSearchFilter collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} onNewSearch={handleNewSearch} onClear={handleClearFilters} onApply={handleApplyFilters} onFiltersChange={setActiveFilters} resetKey={filterResetKey} />}

        {/* ── MAIN RESULTS AREA ───────────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {/* Results top bar */}
          {showResults && (
            <div
              style={{
                backgroundColor: "#fff",
                borderBottom: "1px solid #e5e7eb",
                padding: "12px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "10px",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              {!isLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <span style={{ fontSize: "15px", fontWeight: 200, color: "#111827" }}>{`(${getPaginationRange(totalResults, rowsPerPage, page)})`}</span>
                  <span style={{ fontSize: "22px", fontWeight: 700, color: "#111827" }}>of</span>
                  <span style={{ fontSize: "22px", fontWeight: 700, color: "#111827" }}>{totalResults}</span>
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>Results</span>
                  <span style={{ fontSize: "15px", fontWeight: 200, color: "#111827" }}>{`Showing ${Number(results?.monster?.resumes?.returned || "0")} Candidates`}</span>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Tag value={`Views : ${useageData.viewsRemaining}/${useageData.quota}`} />
              </div>
            </div>
          )}

          {/* Pagination */}
          {showResults && !isLoading && totalResults > 0 && (
            <div
              style={{
                backgroundColor: "#fff",
                borderBottom: "1px solid #e5e7eb",
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Button
                icon="pi pi-angle-double-left"
                className="p-button-text p-button-sm"
                style={{ color: "#374151" }}
                onClick={() => {
                  setPage(1);
                  onPage(1);
                }}
                disabled={page === 1}
              />
              <Button
                icon="pi pi-angle-left"
                className="p-button-text p-button-sm"
                style={{ color: "#374151" }}
                onClick={() => {
                  if (page - 1 > 0) {
                    setPage(page - 1);
                    onPage(page - 1);
                  }
                }}
                disabled={page === 1}
              />
              {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                const pageNum = startPage + i;
                return (
                  <Button
                    key={pageNum}
                    label={String(pageNum)}
                    className="p-button-sm"
                    style={{
                      minWidth: "32px",
                      padding: "4px",
                      backgroundColor: page === pageNum ? "#9333ea" : "transparent",
                      color: page === pageNum ? "#fff" : "#374151",
                      border: page === pageNum ? "none" : "1px solid #d1d5db",
                      fontSize: "13px",
                    }}
                    onClick={() => {
                      setPage(pageNum);
                      onPage(pageNum);
                    }}
                  />
                );
              })}
              <Button
                icon="pi pi-angle-right"
                className="p-button-text p-button-sm"
                style={{ color: "#374151" }}
                onClick={() => {
                  if (page + 1 <= totalPages) {
                    setPage(page + 1);
                    onPage(page + 1);
                  }
                }}
                disabled={page === totalPages}
              />
              <Button
                icon="pi pi-angle-double-right"
                className="p-button-text p-button-sm"
                style={{ color: "#374151" }}
                onClick={() => {
                  setPage(totalPages);
                  onPage(totalPages);
                }}
                disabled={page === totalPages}
              />
            </div>
          )}

          {/* Resume Cards */}
          <div style={{ padding: "16px 24px" }}>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="card" style={{ marginBottom: "12px", padding: 16 }}>
                  <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <Skeleton shape="circle" size="44px" />
                    <div style={{ flex: 1 }}>
                      <Skeleton width="180px" height="16px" style={{ marginBottom: 6 }} />
                      <Skeleton width="120px" height="12px" />
                    </div>
                  </div>
                  <Skeleton width="100%" height="80px" />
                </div>
              ))
            ) : results.monster?.resumes?.resume?.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280", fontSize: "15px" }}>No candidates found. Try adjusting your search criteria.</div>
            ) : (
              results.monster?.resumes?.resume?.map((resume: Resume, index: any) => <ResumeCard key={index} resume={resume} onClick={() => openResume(resume)} searchQuery={booleanSearchQuery} />)
            )}
          </div>
        </div>
      </div>

      <Toast ref={toast} />

      {/* ── Profile Dialog ───────────────────────────────────────────────── */}
      <Dialog
        header={`${selectedResume?.personalData?.name?.first || ""} ${selectedResume?.personalData?.name?.last || ""}`.trim() || "Candidate Profile"}
        visible={profileVisible}
        style={{ width: "90vw", maxWidth: "1400px" }}
        modal
        maximizable
        onHide={() => {
          setProfileVisible(false);
          setSelectedResume(null);
          setProfileLoading(false);
        }}
      >
        {selectedResume ? (
          <>
            {profileLoading && <ProfileSkeleton />}
            <div style={{ display: profileLoading ? "none" : "block" }}>
              <MonsterCandidateProfile visible={profileVisible} singleData={{ resumeID: selectedResume.sid }} usageData={useageData} toast={toast} onLoaded={() => setProfileLoading(false)} searchQuery={booleanSearchQuery} />
            </div>
          </>
        ) : (
          profileLoading && <ProfileSkeleton />
        )}
      </Dialog>
    </>
  );
};

// ─── Resume Card ──────────────────────────────────────────────────────────────

interface IProps {
  resume: Resume;
  onClick: (r: Resume) => void;
  searchQuery: string;
}

const ResumeCard: React.FC<IProps> = ({ resume, onClick, searchQuery }) => {
  const name = `${resume.personalData?.name?.first || ""} ${resume.personalData?.name?.last || ""}`.trim();
  const initials = (resume.personalData?.name?.first?.[0] || "") + (resume.personalData?.name?.last?.[0] || "");
  const location = resume.personalData?.address?.location;
  const title = resume.target?.jobTitle;
  const relocation = resume.target?.relocation?.text;
  const updated = resume.dateModified?.text;
  const firstExp = resume.experiences?.[0];
  const description = firstExp?.description ?? "";
  const companyName = firstExp?.companyName ?? "";
  const expJobTitle = firstExp?.jobTitle ?? "";
  const education = resume.educations && resume.educations.length > 0 ? resume.educations[resume.educations.length - 1] : undefined;
  const auth = resume.workAuths?.[0]?.countryText;
  const source = resume.boards?.[0]?.text;
  const score = Number(resume.relevance || 0) * 10;
  const scoreColor = score >= 8 ? "#16a34a" : score >= 5 ? "#f59e0b" : "#dc2626";

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e9e3f5",
        borderRadius: "12px",
        marginBottom: "12px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #f5f0ff, #faf8ff)",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #C88BFF, #9333EA)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          {initials || "NA"}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => onClick(resume)} style={{ border: "none", background: "none", fontWeight: 700, cursor: "pointer", color: "#111827" }}>
              {name || "Confidential"}
            </button>
            {location && (
              <span
                style={{
                  fontSize: 11,
                  background: "#ede9fe",
                  padding: "2px 8px",
                  borderRadius: 20,
                  color: "#7c3aed",
                }}
              >
                {location}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#555" }}>{title || "—"}</div>
        </div>

        <div
          style={{
            border: `2px solid ${scoreColor}`,
            borderRadius: 20,
            padding: "4px 10px",
            fontWeight: 700,
            color: scoreColor,
            background: "#DCF8E2",
          }}
        >
          {score.toFixed(1)} Match
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 14 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            background: "#f5f0ff",
            padding: 10,
            borderRadius: 8,
          }}
        >
          {relocation && <Tags label="Relocation" value={relocation} color={relocation === "Will Relocate" ? "#16a34a" : "#dc2626"} />}
          <Tags label="Auth" value={auth ? auth : "-"} />
          {updated && <Tags label="Resume Updated" value={updated} />}
          {education?.level && <Tags label="Highest Education" value={education.level ?? "-"} />}
        </div>

        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {title && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: 8,
                padding: "6px 12px",
              }}
            >
              <i className="pi pi-briefcase" style={{ fontSize: 12, color: "#9333ea" }} />
              <span style={{ fontSize: 11, color: "#6b7280" }}>Target Role</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{highlightText(title, searchQuery)}</span>
            </div>
          )}

          {resume.personalData?.address?.city && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: 8,
                padding: "6px 12px",
              }}
            >
              <i className="pi pi-map-marker" style={{ fontSize: 12, color: "#9333ea" }} />
              <span style={{ fontSize: 11, color: "#6b7280" }}>City</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{resume.personalData.address.city}</span>
            </div>
          )}

          {source && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#fdf4ff",
                border: "1px solid #e9d5ff",
                borderRadius: 8,
                padding: "6px 12px",
              }}
            >
              <i className="pi pi-globe" style={{ fontSize: 12, color: "#9333ea" }} />
              <span style={{ fontSize: 11, color: "#6b7280" }}>Source</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#7c3aed" }}>{source}</span>
            </div>
          )}

          {(expJobTitle || companyName || description) && (
            <div
              style={{
                width: "100%",
                marginTop: 4,
                fontSize: 12,
                lineHeight: 1.6,
                background: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              {(expJobTitle || companyName) && (
                <div style={{ marginBottom: 4 }}>
                  {expJobTitle && <span style={{ color: "#111827", fontWeight: 700 }}>{highlightText(expJobTitle, searchQuery)}</span>}
                  {expJobTitle && companyName && <span style={{ color: "#6b7280", margin: "0 6px" }}>@</span>}
                  {companyName && <span style={{ color: "#374151", fontWeight: 500 }}>{companyName}</span>}
                </div>
              )}
              {description && (
                <div style={{ color: "#6b7280" }}>
                  <i className="pi pi-align-left" style={{ fontSize: 11, color: "#9333ea", marginRight: 6 }} />
                  {highlightText(description, searchQuery)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Tags = ({ label, value, color = "#111827" }: { label: string; value: string; color?: string }) => (
  <div style={{ background: "#ede9fe", padding: "5px 10px", borderRadius: 6, fontSize: 12 }}>
    <span style={{ color: "#888", marginRight: 4 }}>{label}</span>
    <span style={{ fontWeight: 700, color }}>{value}</span>
  </div>
);
